/**
 * Inhalte der öffentlichen Unterstützer:innen-Seite.
 *
 * ⚠️ ENTWURF — von JAA fachlich zu prüfen.
 *
 * Die Texte sind aus der Anforderungsbeschreibung abgeleitet
 * (JAA_2025_Funktionen_Unterstützer) und inhaltlich plausibel, aber nicht von
 * Junior Achievement freigegeben. Alles mit PRUEFEN markierte enthält
 * Aussagen, die ich nicht aus der Spec belegen kann und die vor
 * Veröffentlichung bestätigt oder ersetzt werden müssen.
 *
 * Die Texte liegen bewusst hier und nicht in ger.js/en.js: Es sind
 * redaktionelle Inhalte, die JAA ändern wird, keine UI-Beschriftungen.
 */

export const REGISTER_CONTENT = {
	ger: {
		eyebrow: 'Junior Achievement Austria',
		// Muss ein zusammenhaengender String bleiben und in genau einem Element
		// gerendert werden: register.test.js prueft textContent auf diese
		// Zeichenkette. Ein Umbruch ueber zwei Elemente wuerde das Leerzeichen
		// an der Bruchstelle verschlucken. Der Zeilenumbruch kommt aus dem CSS.
		headline: 'Werden Sie ein Teil des Erfolges — werden Sie Unterstützer:in.',
		intro: 'Junior Companies sind echte Schülerunternehmen: Jugendliche gründen, führen und liquidieren sie innerhalb eines Schuljahres. Was ihnen dabei am meisten hilft, sind Menschen aus der Praxis.',
		heroCta: 'Jetzt registrieren',
		heroScroll: 'Mehr erfahren',

		galleryTitle: 'Das entsteht dabei',
		// PRUEFEN: Die Bildunterschriften sind aus dem abgelesen, was auf den
		// Fotos selbst zu sehen ist (Messeschilder, Bannertexte, Urkunden).
		// Veranstaltung und Jahr sind teilweise erschlossen und von JAA zu
		// bestaetigen, bevor die Seite oeffentlich geht.
		galleryNeedsReview: true,
		gallery: [
			{ file: 'messe-mode', caption: 'Junior Messe: ein Schülerunternehmen präsentiert seine Produkte' },
			{ file: 'siegerehrung', caption: 'Siegerehrung beim Junior Österreichwettbewerb' },
			{ file: 'messe-upcycling', caption: 'Upcycling-Produkte am eigenen Messestand' },
			{ file: 'europafinale', caption: 'Das österreichische Team beim Europafinale' },
			{ file: 'messe-technik', caption: 'Vom Prototyp zum verkaufsfertigen Produkt' },
			{ file: 'gewinner', caption: 'Übergabe des ersten Platzes an eine Junior Company' }
		],

		rolesTitle: 'Drei Arten zu unterstützen',
		rolesHint: 'Sie können mehrere Rollen gleichzeitig übernehmen.',
		roles: [
			{
				key: 'EXPERT',
				title: 'Wirtschaftsexpert:in',
				text: 'Sie begleiten eine oder mehrere Junior Companies über das Schuljahr und stehen bei betriebswirtschaftlichen Fragen zur Seite. Die Schüler:innen fragen Sie über die Datenbank an, Sie entscheiden selbst, ob Sie annehmen.'
			},
			{
				key: 'JURY',
				title: 'Jury-Mitglied',
				text: 'Sie bewerten Projekte und Pitches bei Wettbewerben und Veranstaltungen. Der Einsatz ist punktuell und wird von Junior Achievement angefragt.'
			},
			{
				key: 'TRAINER',
				title: 'Trainer:in',
				text: 'Sie halten Workshops oder Trainings zu Ihren Fachthemen. Ihr Angebot hinterlegen Sie in Ihrem Profil, Schulen und Junior Achievement können darauf zugreifen.'
			}
		],

		stepsTitle: 'So läuft es ab',
		steps: [
			'Sie registrieren sich mit ein paar Angaben zu Ihrer Person und wählen Ihre Rollen.',
			'Junior Achievement prüft Ihre Anmeldung und schaltet Ihr Profil frei.',
			'Sie ergänzen Ihr Profil: Fachgebiete, Regionen und Schultypen, die Sie unterstützen möchten.',
			'Ab dann sind Sie für Schulen sichtbar und erhalten Anfragen.'
		],

		/*
		 * Was aus dem Programm schon geworden ist — mit dem Verweis auf die
		 * öffentliche Junior-Bilanz. Es steht bewusst vor „Drei Arten zu
		 * unterstützen": Erst sehen, worauf man sich einlässt, dann lesen, wie
		 * man dazukommt.
		 *
		 * Die Zahlen stammen aus derselben Auswertung wie der Bericht
		 * (Auswertungsstand 24.08.2026, exkl. Junior Mini Companies). Wer sie
		 * ändert, ändert sie im Bericht mit — sonst stehen zwei Wahrheiten
		 * nebeneinander.
		 */
		impactTitle: 'Was daraus schon geworden ist',
		impactLead:
			'Junior Achievement Austria bringt Schülerunternehmen und Wirtschaft seit Jahren zusammen. Wie viel daraus in einem einzigen Schuljahr entsteht, steht in der Junior-Bilanz 2025/26 — offen einsehbar, mit Zahlen aus der Junior Datenbank.',
		impactFigures: [
			{ value: '592', label: 'Junior Companies im Schuljahr 2025/26' },
			{ value: '4.966', label: 'Schüler:innen an 185 Schulen' },
			{ value: '281', label: 'Unternehmen bis zur Urkunde' },
			{ value: '375', label: 'Wirtschaftsexpert:innen im Verzeichnis' }
		],
		impactCta: 'Junior-Bilanz 2025/26 ansehen',
		impactNote: 'Öffentliche Fassung, ohne finanzielle Ergebnisse · exkl. Junior Mini Companies',
		impactJoin:
			'Die 375 Wirtschaftsexpert:innen in dieser Bilanz sind Menschen wie Sie. Hier schließen Sie sich an:',
		impactJoinCta: 'Zur Registrierung',

		commitmentTitle: 'Was auf Sie zukommt',
		// PRUEFEN: Der zeitliche Aufwand steht nirgends in der Spec. Die Angabe
		// ist eine Annahme und muss von JAA bestätigt oder korrigiert werden.
		commitment:
			'Sie bestimmen den Umfang selbst. Sie entscheiden bei jeder Anfrage neu, ob Sie annehmen, und können Ihr Profil jederzeit vorübergehend auf „nicht verfügbar" stellen, ohne Ihren Zugang zu verlieren.',
		commitmentNeedsReview: true,

		privacyTitle: 'Ihre Daten',
		privacy:
			'Für Lehrkräfte und Schüler:innen sind ausschließlich Wirtschaftsexpert:innen sichtbar, und auch dort nur Ihr fachliches Profil — keine Kontaktdaten. Jury- und Trainer:innen-Profile werden nur intern von Junior Achievement verwaltet. Sie können Ihr Profil jederzeit löschen; Ihre personenbezogenen Daten werden dann anonymisiert.',

		formEyebrow: 'Jetzt mitmachen',
		formTitle: 'Registrierung',
		formHint: 'Ihre Angaben werden von Junior Achievement geprüft, bevor Ihr Profil sichtbar wird.',
		formRequired: 'Mit * markierte Felder sind Pflichtfelder.',
		// Drei Bloecke, damit das Formular nicht als eine Wand aus acht Feldern
		// erscheint. Die Reihenfolge folgt dem, was Besucher:innen erwarten:
		// erst wer sie sind, dann der Zugang, dann das Engagement.
		formGroups: [
			{
				title: 'Zu Ihrer Person',
				hint: 'Unternehmen und Funktion sind freiwillig — sie helfen uns bei der Einordnung Ihrer Anmeldung.'
			},
			{
				title: 'Ihre Zugangsdaten',
				hint: 'Mit dieser E-Mail-Adresse melden Sie sich später an.'
			},
			{
				title: 'Ihr Engagement',
				hint: 'Alle Angaben können Sie in Ihrem Profil jederzeit ändern.'
			},
			{
				title: 'Junior Company Teilnahme'
			}
		],
		formPasswordHint: 'Mindestens 8 Zeichen.',
		formRolesHint: 'Mehrfachauswahl möglich.',
		formSubmitHint: 'Kein Aufwand ohne Ihr Zutun: Sie entscheiden bei jeder Anfrage neu.',

		successTitle: 'Vielen Dank für Ihre Anmeldung',
		successText:
			'Wir haben Ihre Registrierung erhalten. Junior Achievement prüft sie und schaltet Ihr Profil anschließend frei. Sie erhalten eine E-Mail, sobald es soweit ist.',
		successHint:
			'Bitte bestätigen Sie zusätzlich Ihre E-Mail-Adresse über den Link, den wir Ihnen gerade geschickt haben.'
	},

	en: {
		eyebrow: 'Junior Achievement Austria',
		headline: 'Be part of the success — become a supporter.',
		intro: 'Junior Companies are real student-run businesses: young people found, run and wind them up within a single school year. What helps them most are people with hands-on experience.',
		heroCta: 'Register now',
		heroScroll: 'Find out more',

		galleryTitle: 'This is what comes out of it',
		galleryNeedsReview: true,
		gallery: [
			{ file: 'messe-mode', caption: 'Junior trade fair: a student company presents its products' },
			{ file: 'siegerehrung', caption: 'Award ceremony at the Junior Austria competition' },
			{ file: 'messe-upcycling', caption: 'Upcycled products at their own trade fair stand' },
			{ file: 'europafinale', caption: 'The Austrian team at the European final' },
			{ file: 'messe-technik', caption: 'From prototype to market-ready product' },
			{ file: 'gewinner', caption: 'Handing over first place to a Junior Company' }
		],

		rolesTitle: 'Three ways to support',
		rolesHint: 'You can take on several roles at the same time.',
		roles: [
			{
				key: 'EXPERT',
				title: 'Economic expert',
				text: 'You accompany one or more Junior Companies through the school year and advise on business questions. Students request you through the database — you decide whether to accept.'
			},
			{
				key: 'JURY',
				title: 'Jury member',
				text: 'You assess projects and pitches at competitions and events. The commitment is occasional and requested by Junior Achievement.'
			},
			{
				key: 'TRAINER',
				title: 'Trainer',
				text: 'You run workshops or training sessions in your field. You record your offering in your profile, where schools and Junior Achievement can find it.'
			}
		],

		stepsTitle: 'How it works',
		steps: [
			'Register with a few details about yourself and choose your roles.',
			'Junior Achievement reviews your registration and activates your profile.',
			'You complete your profile: fields of expertise, regions and school types you would like to support.',
			'From then on schools can see you and send you requests.'
		],

		impactTitle: 'What has come of it so far',
		impactLead:
			'Junior Achievement Austria has been bringing student companies and business together for years. How much comes out of a single school year is in the Junior-Bilanz 2025/26 — openly readable, with figures from the Junior database.',
		impactFigures: [
			{ value: '592', label: 'Junior Companies in 2025/26' },
			{ value: '4,966', label: 'students at 185 schools' },
			{ value: '281', label: 'companies that reached the certificate' },
			{ value: '375', label: 'business experts in the directory' }
		],
		impactCta: 'Read the Junior-Bilanz 2025/26',
		impactNote: 'Public edition, without financial results · excl. Junior Mini Companies',
		impactJoin: 'The 375 business experts in that report are people like you. This is where you join:',
		impactJoinCta: 'Go to registration',

		commitmentTitle: 'What to expect',
		commitment:
			'You set the scope yourself. You decide on every single request whether to accept, and you can set your profile to unavailable at any time without losing your account.',
		commitmentNeedsReview: true,

		privacyTitle: 'Your data',
		privacy:
			'Teachers and students only ever see economic experts, and only your professional profile — no contact details. Jury and trainer profiles are managed internally by Junior Achievement. You can delete your profile at any time; your personal data will then be anonymised.',

		formEyebrow: 'Get involved',
		formTitle: 'Registration',
		formHint: 'Your details will be reviewed by Junior Achievement before your profile becomes visible.',
		formRequired: 'Fields marked with * are required.',
		formGroups: [
			{
				title: 'About you',
				hint: 'Company and position are optional — they help us place your registration.'
			},
			{
				title: 'Your account',
				hint: 'You will use this email address to sign in later.'
			},
			{
				title: 'Your involvement',
				hint: 'You can change all of this in your profile at any time.'
			},
			{
				title: 'Junior Company participation'
			}
		],
		formPasswordHint: 'At least 8 characters.',
		formRolesHint: 'You can pick more than one.',
		formSubmitHint: 'Nothing happens without you: you decide on every single request.',

		successTitle: 'Thank you for signing up',
		successText:
			'We have received your registration. Junior Achievement will review it and activate your profile. You will get an email as soon as that is done.',
		successHint: 'Please also confirm your email address using the link we just sent you.'
	}
}

/** Liefert die Inhalte zur aktiven Sprache, mit Deutsch als Rückfall. */
export const getRegisterContent = language => REGISTER_CONTENT[String(language).startsWith('en') ? 'en' : 'ger']
